import React from 'react';
import ReactDOM from 'react-dom';
import { useState, useEffect, useRef, useMemo } from "react"
import { Ic } from "./Ic.jsx";
import { photoLog, photoLogClear } from "../utils.js";
import { useSt, RECOVERY_MODE, supaGet } from "../supabase.js";
import { DS, DG, DP, DC, DB, DST, DPJ, DL, DU, DR, DM, DTC, DAL, DREP, RGPD_DOC_V1 } from "../constants.js";
import { fname, migrerContrats } from "../utils.js";
import { migrerCompteurs } from "../facturxCore.js";
import { CONSIGNE_SETTINGS_DEFAUT, CONSIGNE_CATALOGUE_DEFAUT } from "../consigneCore.js";
import { SecFacturation, TaxeSejourView, nbFacturesEnRetard } from "./Facturation.jsx";
import { TARIFS_EXP_DEFAULT, AdminGrilleTarifs } from "./GerantClientUI.jsx";
// SecProp/SecAnalyses chargés en lazy (onglet Propriétaires)
const SecProp = React.lazy(() => import("./SecProp.jsx").then(m => ({ default: m.SecProp })));
const SecAnalyses = React.lazy(() => import("./SecProp.jsx").then(m => ({ default: m.SecAnalyses })));
import { VueGen } from "./AdminUI.jsx";
// SecRH chargé en lazy (onglet RH)
const SecRH = React.lazy(() => import("./SecRH.jsx").then(m => ({ default: m.SecRH })));
// SecPrest chargé en lazy (onglet Prestataires)
const SecPrest = React.lazy(() => import("./SecPrest.jsx").then(m => ({ default: m.SecPrest })));
import { GerantUI } from "./GerantUI.jsx";
import { CalendrierSejours } from "./CalendrierSejours.jsx";
import { StaffUI } from "./StaffUI.jsx";
import { ProvUI } from "./ProvUI.jsx";
import { ClientUI } from "./ClientUI.jsx";
import { ErrorBoundary } from "./ErrorBoundary.jsx";
import { QueueRecovery } from "./QueueRecovery.jsx";
import { autoReplayAls7, autoReplayControles7 } from "../queueAutoReplay.js";
import { purgeExpired } from "../localQueue.js";
import { defaultNotifConfig, setNotifContext } from "../notifBus.js";
import { RecoveryScreen, LoginScreen, useVoiceMode, VoiceListener, VoiceToggleButton, PendingReportBanner, AssistanceBanner, DureeAnomBanner, SyncErrorBanner, ChangePasswordModal, NotificationButton, SecCalendrier, SecNotifs, useNotifToasts, NotifToastStack, SecObjets, SimulateurExpPublic } from "./GerantClientUI.jsx";
import { SecRapports, SecControles } from "./SecRapports.jsx";
import { SecAnomaliesSync } from "./SecAnomaliesSync.jsx";
import { SecFinJournee } from "./SecFinJournee.jsx";
// Module Proposition tarifaire (devis) — lazy : écran d'avant-vente, jamais
// ouvert pendant le travail terrain, inutile de l'embarquer dans le nœud central.
const SecDevis = React.lazy(() => import("./SecDevis.jsx").then(m => ({ default: m.SecDevis })));
const SecDevisConfig = React.lazy(() => import("./SecDevisConfig.jsx").then(m => ({ default: m.SecDevisConfig })));
// Page publique d'acceptation du devis (Lot 2) — lazy pour ne jamais rejoindre
// le nœud central : c'est un écran d'avant-vente ouvert par des clients externes,
// jamais par les salariées sur tablette.
const DevisPublic = React.lazy(() => import("./DevisPublic.jsx").then(m => ({ default: m.DevisPublic })));
import { MaintenanceScreen, SecMaintenance, isBlocked } from "./Maintenance.jsx";
// SecJournal chargé en lazy (onglet Journal)
const SecJournal = React.lazy(() => import("./SecJournal.jsx").then(m => ({ default: m.SecJournal })));
// SecStatutAppts chargé en lazy (onglet Statut)
const SecStatutAppts = React.lazy(() => import("./SecStatut.jsx").then(m => ({ default: m.SecStatutAppts })));
// Coque module Consigne (lazy : activite a part entiere, jamais chargee sur le terrain).
const SecConsigne = React.lazy(() => import("./SecConsigne.jsx").then(m => ({ default: m.SecConsigne })));
// UI publique de reservation en ligne Consigne (route /consigne, lazy, jamais dans le noeud central).
const ConsigneClientUI = React.lazy(() => import("./ConsigneClientUI.jsx").then(m => ({ default: m.ConsigneClientUI })));
// Page publique de paiement d'une reservation prise par telephone (lot T3, route /consigne?payer=CODE, lazy, hors noeud central).
const PagePaiementResa = React.lazy(() => import("./PagePaiementResa.jsx").then(m => ({ default: m.PagePaiementResa })));
// Écran comptoir Ma Consigne (route /consigne-comptoir, lazy, SANS connexion —
// voir ConsigneComptoir.jsx pour la justification sécurité/chargement allégé).
const ConsigneComptoir = React.lazy(() => import("./ConsigneComptoir.jsx").then(m => ({ default: m.ConsigneComptoir })));


// ═══════════════════════════════════════════════════════════════════
// SCRIPT 9 — APP RACINE + RENDER (doit s'exécuter en dernier : consomme
// tout le scope global défini par les scripts précédents)
// ═══════════════════════════════════════════════════════════════════

// ══════════════ APP ══════════════
// ─── BOÎTE NOIRE : capteur global d'erreurs ───
// But : afficher À L'ÉCRAN toute erreur JS, y compris les erreurs ASYNCHRONES (promesses, .then,
// callbacks) que l'ErrorBoundary React ne peut PAS attraper. Permet de diagnostiquer un crash
// (ex. page blanche à la prise de photo) sans brancher le téléphone à un ordinateur.
// La bannière est fixe, persistante et copiable d'un clic. N'altère aucune logique métier.
// ─── Helper logAction — enregistre une action dans journal7 ───────────────────
// Appelé depuis SecStatut, SecRapports, Facturation via P.logAction(...)
// entry: { action, qui, role, objet?, cible?, bid?, fid?, detail? }
export function logAction(setJournal, entry) {
  setJournal(prev => [
    ...(Array.isArray(prev) ? prev : []),
    { id: 'j' + Date.now() + Math.random().toString(36).slice(2,6), ts: new Date().toISOString(), ...entry }
  ]);
}

// ─── Journal persistant de la prise de photo (diagnostic crash mémoire / OOM) ───
// Un crash OOM tue l'onglet AVANT que window.onerror puisse réagir. Parade : on écrit chaque
// étape dans localStorage au fur et à mesure. Après crash + rechargement, ErrorBlackbox relit
// ce journal : la dernière ligne = l'étape exacte où l'onglet est mort.
// photoLog/photoLogClear déplacés vers utils.js (évite cycle d'imports)

// ─── Compteur mémoire (diagnostic) ───
// Affiche en bas à gauche le tas mémoire JS utilisé / limite du navigateur. Sur les appareils
// à faible plafond (ex. 919 Mo), permet de voir en direct si l'app s'approche du seuil de crash.
// Visible uniquement si performance.memory existe (Chrome/Android). N'altère aucune logique.
function MemoryGauge(){
  const[m,setM]=useState(null);
  // Visibilité réservée aux admins (flag 'gauge_show' écrit par App selon le rôle).
  // Par défaut masqué (ex. écran de login) : on n'affiche les chiffres mémoire qu'en admin.
  const[show,setShow]=useState(()=>{try{return localStorage.getItem('gauge_show')==='1';}catch{return false;}});
  useEffect(()=>{
    const sync=()=>{try{setShow(localStorage.getItem('gauge_show')==='1');}catch{}};
    window.addEventListener('diag-role-change',sync);
    window.addEventListener('storage',sync);
    return()=>{window.removeEventListener('diag-role-change',sync);window.removeEventListener('storage',sync);};
  },[]);
  useEffect(()=>{
    // Ne lance le polling mémoire que si la jauge est visible (admin) : aucun coût pour les salariés.
    if(!show)return;
    if(!(performance&&performance.memory))return;
    const tick=()=>{const pm=performance.memory;setM({u:Math.round(pm.usedJSHeapSize/1048576),l:Math.round(pm.jsHeapSizeLimit/1048576)});};
    tick();const id=setInterval(tick,2000);
    return()=>clearInterval(id);
  },[show]);
  if(!show||!m)return null;
  const pct=Math.round(m.u/m.l*100);
  const col=pct>80?'#dc2626':pct>60?'#d97706':'#16a34a';
  return(<div style={{position:'fixed',left:6,bottom:6,zIndex:99998,background:'rgba(0,0,0,.78)',color:col,padding:'3px 8px',borderRadius:6,fontSize:11,fontFamily:'monospace',fontWeight:700,pointerEvents:'none'}}>
    {m.u}/{m.l} Mo · {pct}%
  </div>);
}

function ErrorBlackbox(){
  const[errs,setErrs]=useState(()=>{
    // Le journal photo n'est remonté que s'il contient autre chose que du BRUIT D'AMBIANCE.
    // Pourquoi : realtime.js écrit « Realtime suspendu (caméra) / relancé » dès que la caméra
    // s'ouvre, même si aucune photo n'est finalement prise. Comme photoLogClear() n'est appelé
    // qu'à l'enregistrement réussi d'une photo, une caméra ouverte puis annulée laissait deux
    // lignes orphelines que rien n'effaçait — et la bannière rouge « à transmettre à la
    // direction » s'affichait pour un non-événement. Une alarme qui crie à vide finit par être
    // ignorée le jour où elle a raison. On efface donc silencieusement ces journaux-là.
    //
    // Règle volontairement inversée : on énumère le BRUIT, pas les événements réels. Ainsi tout
    // nouveau photoLog() ajouté un jour dans le code sera remonté par défaut. L'inverse (lister
    // les événements réels) deviendrait faux en silence à la première trace oubliée.
    // La COLLECTE est inchangée : aucune capacité de diagnostic n'est perdue.
    const BRUIT=/Realtime (suspendu|relancé)/i;
    try{
      const diag=JSON.parse(localStorage.getItem('photo_diag')||'[]');
      if(!diag.length) return [];
      if(diag.every(l=>BRUIT.test(String(l)))){localStorage.removeItem('photo_diag');return [];}
      return['── Journal de la dernière prise de photo (avant rechargement) ──',...diag];
    }catch(_){}
    return [];
  });
  const pushErr=React.useCallback((label,detail)=>{
    setErrs(prev=>{
      const line=`[${new Date().toLocaleTimeString('fr-FR')}] ${label} : ${detail}`;
      // évite les doublons consécutifs identiques
      if(prev.length&&prev[prev.length-1]===line) return prev;
      return [...prev,line].slice(-20); // garde les 20 dernières
    });
  },[]);
  useEffect(()=>{
    const onError=(msg,src,lineno,colno,error)=>{
      const detail=(error&&error.stack)?error.stack:`${msg} @ ${src||'?'}:${lineno||'?'}:${colno||'?'}`;
      pushErr('Erreur JS',String(detail).slice(0,600));
      return false; // ne bloque pas le comportement par défaut
    };
    const onRejection=ev=>{
      const r=ev&&ev.reason;
      const detail=(r&&r.stack)?r.stack:(r&&r.message)?r.message:String(r);
      pushErr('Promesse rejetée',String(detail).slice(0,600));
    };
    const errHandler=e=>onError(e.message,e.filename,e.lineno,e.colno,e.error);
    window.addEventListener('error',errHandler);
    window.addEventListener('unhandledrejection',onRejection);
    return()=>{
      window.removeEventListener('error',errHandler);
      window.removeEventListener('unhandledrejection',onRejection);
    };
  },[pushErr]);
  // Visibilité de la bannière selon le rôle (écrit par App dans 'diag_show').
  // '0' = propriétaire/client → on n'affiche jamais la bannière (la collecte continue malgré tout).
  // Par défaut (flag absent, ex. écran de login), on autorise l'affichage : utile pour le debug interne.
  const[showBanner,setShowBanner]=useState(()=>{try{return localStorage.getItem('diag_show')!=='0';}catch{return true;}});
  useEffect(()=>{
    const sync=()=>{try{setShowBanner(localStorage.getItem('diag_show')!=='0');}catch{}};
    window.addEventListener('diag-role-change',sync);
    window.addEventListener('storage',sync);
    return()=>{window.removeEventListener('diag-role-change',sync);window.removeEventListener('storage',sync);};
  },[]);

  if(errs.length===0||!showBanner) return null;
  const fullText=errs.join('\n');
  const copy=()=>{
    try{
      if(navigator.clipboard&&navigator.clipboard.writeText){
        navigator.clipboard.writeText(fullText).then(()=>window.alert('Erreurs copiées. Tu peux les coller dans un message à la direction.'),()=>window.alert('Copie impossible — fais une capture d\'écran de la bannière.'));
      }else{
        window.alert('Copie automatique non disponible — fais une capture d\'écran de la bannière.');
      }
    }catch(e){window.alert('Copie impossible — fais une capture d\'écran de la bannière.');}
  };
  return(<div style={{position:'fixed',left:0,right:0,bottom:0,zIndex:99999,background:'#7f1d1d',color:'#fff',padding:'10px 12px',boxShadow:'0 -4px 14px rgba(0,0,0,.35)',maxHeight:'45vh',overflowY:'auto',fontSize:12}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,marginBottom:6}}>
      <strong style={{fontSize:13}}>🐞 Diagnostic ({errs.length}) — à transmettre à la direction</strong>
      <div style={{display:'flex',gap:6}}>
        <button onClick={copy} style={{background:'#fff',color:'#7f1d1d',border:'none',borderRadius:6,padding:'5px 10px',fontWeight:700,fontSize:12}}>Copier</button>
        <button onClick={()=>{try{localStorage.removeItem('photo_diag');}catch(_){}setErrs([]);}} style={{background:'transparent',color:'#fff',border:'1px solid #fff',borderRadius:6,padding:'5px 10px',fontSize:12}}>Masquer</button>
      </div>
    </div>
    <pre style={{whiteSpace:'pre-wrap',wordBreak:'break-word',margin:0,fontFamily:'monospace',fontSize:11,lineHeight:1.5}}>{fullText}</pre>
  </div>);
}

function AdminNav({sec,setSec,ctGen,ctSta,ctRh,ctObj,ctRap,ctCtrl,ctAno,ctFinJ,ctFac,ctMaint}){
  const[open,setOpen]=React.useState(null);       // id du groupe ouvert
  const[dropPos,setDropPos]=React.useState({x:0,y:0}); // position calculée
  const btnRefs=React.useRef({});                 // ref par groupe id
  const GROUPS=[
    {type:'group', id:'vuegen', e:'🏠', l:'Vue générale', ct:ctGen+ctSta, items:[
      {id:'gen',  e:'🏠', l:'Accueil',        ct:ctGen},
      {id:'sta',  e:'📊', l:'Statut apparts', ct:ctSta},
      {id:'sej',  e:'📆', l:'Séjours',        ct:0},
      {id:'com',  e:'💬', l:'Commentaires',   ct:0},
      {id:'msg',  e:'✉️', l:'Messages',       ct:0},
    ]},
    {type:'group', id:'equipes', e:'👷', l:'Équipes', ct:ctRh, items:[
      {id:'rh',   e:'👷', l:'RH',             ct:ctRh},
      {id:'pre',  e:'🔧', l:'Prestataires',   ct:0},
    ]},
    {type:'group', id:'clients', e:'🏠', l:'Clients', ct:ctFac, items:[
      {id:'pro',  e:'🏠', l:'Propriétaires & logements', ct:0},
      {id:'fac',  e:'🧾', l:'Facturation',    ct:ctFac},
      {id:'ts',   e:'🏛️',l:'Taxe de séjour',  ct:0},
      {id:'dev',  e:'📄', l:'Propositions tarifaires', ct:0},
      {id:'devc', e:'🗂️',l:'Catalogue prestations',   ct:0},
    ]},
    {type:'group', id:'terrain', e:'🏡', l:'Terrain', ct:ctObj+ctRap+ctCtrl+ctAno+ctFinJ, items:[
      {id:'obj',  e:'📦', l:'Objets trouvés', ct:ctObj},
      {id:'exp',  e:'⚙️', l:'Tarifs expédition',ct:0},
      {id:'rap',  e:'📋', l:'Rapports',       ct:ctRap},
      {id:'ctrl', e:'🔍', l:'Contrôles',      ct:ctCtrl},
      {id:'anos', e:'⚠️', l:'Anomalies sync', ct:ctAno},
      {id:'finj', e:'📋', l:'Fin de journée',  ct:ctFinJ},
    ]},
    {type:'group', id:'maconsigne', e:'🧳', l:'Ma Consigne', ct:0, items:[
      {id:'consigne', e:'🧳', l:'Ouvrir le module', ct:0},
    ]},
    {type:'group', id:'centre', e:'⚙️', l:'Centre de contrôle', ct:0, items:[
      {id:'notif',e:'🔔', l:'Notifications',  ct:0},
      {id:'cal',  e:'📅', l:'Calendrier',     ct:0},
      {id:'ana',  e:'📊', l:'Analyses',       ct:0},
      {id:'log',  e:'📋', l:'Journal',        ct:0},
      {id:'maint',e:'🚧', l:'Maintenance',    ct:ctMaint},
    ]},
  ];
  const toggleGroup=(gid,btnEl)=>{
    if(open===gid){setOpen(null);return;}
    const r=btnEl.getBoundingClientRect();
    setDropPos({x:r.left+window.scrollX, y:r.bottom+window.scrollY+6});
    setOpen(gid);
  };
  const go=id=>{setSec(id);setOpen(null);};
  React.useEffect(()=>{
    if(!open)return;
    const h=e=>{
      // fermer si clic en dehors du dropdown ET en dehors du bouton déclencheur
      const btn=btnRefs.current[open];
      const drop=document.getElementById('mnav-drop');
      if(btn&&btn.contains(e.target))return;
      if(drop&&drop.contains(e.target))return;
      setOpen(null);
    };
    document.addEventListener('mousedown',h);
    return()=>document.removeEventListener('mousedown',h);
  },[open]);
  const openGroup=GROUPS.find(g=>g.id===open);
  return(
    <>
      <nav className="mnav">
        {GROUPS.map(g=>{
          const childActive=g.items.some(it=>it.id===sec);
          const isOpen=open===g.id;
          return(
            <button
              key={g.id}
              ref={el=>btnRefs.current[g.id]=el}
              onClick={e=>toggleGroup(g.id,e.currentTarget)}
              className={`mnb ${childActive||isOpen?'on':''}`}
            >
              <span>{g.e}</span> {g.l}
              {(g.ct||0)>0&&<span className="nb">{g.ct}</span>}
              <span style={{fontSize:9,marginLeft:2,opacity:.7}}>{isOpen?'▲':'▼'}</span>
            </button>
          );
        })}
      </nav>
      {open&&openGroup&&ReactDOM.createPortal(
        <div id="mnav-drop" className="mndropdown" style={{position:'absolute',left:dropPos.x,top:dropPos.y}}>
          {openGroup.items.map(it=>(
            <button key={it.id} onClick={()=>go(it.id)} className={`mnditem ${sec===it.id?'on':''}`}>
              <span>{it.e}</span> {it.l}{it.ct>0&&<span className="nb">{it.ct}</span>}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

function App(){
  // Au démarrage, récupérer le rôle si on vient de se connecter (cf. LoginScreen)
  const initialSess=(()=>{
    try{
      const pending=sessionStorage.getItem('pending_role');
      if(pending){sessionStorage.removeItem('pending_role');return JSON.parse(pending);}
    }catch{}
    return null;
  })();
  const[sess,setSess]=useSt('sess7',initialSess);
  // Bannière diagnostic (ErrorBlackbox) : on la réserve aux comptes internes (admin/gérant/staff).
  // Un propriétaire (type 'client') ne doit jamais la voir. Le diagnostic continue de collecter
  // en arrière-plan quel que soit le rôle ; seul l'affichage de la bannière est conditionné ici.
  // ErrorBlackbox étant monté à la racine (hors App), il lit ce flag dans localStorage.
  useEffect(()=>{
    try{
      const interne=sess&&sess.type&&sess.type!=='client';
      localStorage.setItem('diag_show',interne?'1':'0');
      // Jauge mémoire (MemoryGauge) : réservée aux admins uniquement (pas gérant/staff/client).
      // Évite d'inquiéter les salariés avec des chiffres mémoire sur appareils modestes.
      const isAdmin=sess&&sess.type==='admin';
      localStorage.setItem('gauge_show',isAdmin?'1':'0');
      // Notifie ErrorBlackbox + MemoryGauge du changement (même onglet : pas d'event 'storage' automatique).
      window.dispatchEvent(new Event('diag-role-change'));
    }catch(_){}
  },[sess&&sess.type]);
  // Mise à jour forcée à distance (v157, 11/08/2026) : le service worker relaie
  // un postMessage({type:'FORCE_RELOAD'}) quand ce même onglet est visible au
  // moment d'un push type:'force_reload' (voir public/sw.js). Monté une seule
  // fois, indépendamment de la session — un onglet peut recevoir ce message
  // avant même que quiconque soit connecté (écran de login compris).
  useEffect(()=>{
    if(!('serviceWorker' in navigator))return;
    const onMsg=(e)=>{if(e.data&&e.data.type==='FORCE_RELOAD'){console.log('[App] FORCE_RELOAD reçu du SW, rechargement');window.location.reload();}};
    navigator.serviceWorker.addEventListener('message',onMsg);
    return()=>navigator.serviceWorker.removeEventListener('message',onMsg);
  },[]);
  // Lecture vocale (accessibilité salariés non lecteurs). State persisté par appareil.
  const[voiceMode,setVoiceMode]=useVoiceMode();
  const[showChangePwd,setShowChangePwd]=useState(false);
  // Mode "Voir comme" (impersonation) : permet à l'admin de basculer dans l'interface d'un salarié/prestataire/etc.
  // pour tester ou faire du support. La session admin est conservée et peut être restaurée d'un clic.
  // Cette session de bascule est stockée en sessionStorage donc elle est perdue au refresh (sécurité).
  const[impersonating,setImpersonating]=useState(()=>{
    try{const s=sessionStorage.getItem('impersonate_origin');return s?JSON.parse(s):null;}catch{return null;}
  });
  const viewAs=(type,id)=>{
    // Sauvegarde la session admin actuelle, puis bascule
    try{sessionStorage.setItem('impersonate_origin',JSON.stringify(sess));}catch{}
    setImpersonating(sess);
    setSess({type,id});
  };
  const stopViewAs=()=>{
    if(!impersonating)return;
    setSess(impersonating);
    setImpersonating(null);
    try{sessionStorage.removeItem('impersonate_origin');}catch{}
  };
  const[staff,setStaff,staffLd]=useSt('staff7',DS);
  const[gerants,setGerants,gerantsLd]=useSt('gerants1',DG);
  const[prov,setProv,provLd]=useSt('prov7',DP);
  const[clients,setClients,clientsLd]=useSt('clients7',DC);
  const[biens,setBiens,biensLoaded]=useSt('biens8',DB);
  const[st,setSt,stLd]=useSt('st7',DST);
  const[pj,setPj,pjLd]=useSt('pj7',DPJ);
  const[leave,setLeave,leaveLd]=useSt('leave7',DL);
  const[unavail,setUnavail,unavailLd]=useSt('unavail7',DU);
  const[req,setReq,reqLd]=useSt('req7',DR);
  const[msgs,setMsgs,msgsLd]=useSt('msgs7',DM);
  const[msgsLu,setMsgsLu,msgsLuLd]=useSt('msgsLu7',[]);  // IDs des messages lus par l'admin — clé séparée, jamais écrasée par Franck
  const[journal,setJournal,journalLd]=useSt('journal7',[]);  // Journal d'audit append-only
  const[tc,setTc,tcLd,setTcSynced]=useSt('tc7',DTC);
  const[als,setAls,alsLd,setAlsSynced]=useSt('als7',DAL);
  const[rep,setRep,repLd]=useSt('rep7',DREP);
  const[sejours,setSejours,sejoursLd]=useSt('sejours7',[],{defer:true});
  const[objets,setObjets,objetsLd]=useSt('objets7',[]);
  const[otBacs,setOtBacs,otBacsLd]=useSt('otBacs7',[]);
  const[tarifsExp,setTarifsExp,tarifsExpLd]=useSt('tarifsExp7',TARIFS_EXP_DEFAULT);
  const[controles,setControles,controlesLd]=useSt('controles7',[],{defer:true});
  const[commentaires,setCommentaires,commentairesLd]=useSt('commentaires8',[],{defer:true});
  const[notifConfig,setNotifConfig,notifConfigLd]=useSt('notifConfig',defaultNotifConfig());
  // Mode maintenance ciblé (jamais l'admin) — voir Maintenance.jsx pour le détail du format
  const[maintenance,setMaintenance,maintenanceLd]=useSt('maintenance7',{on:false,targets:{staff:true,gerant:true,provider:true,client:true},messageId:'menage'});
  const[feries,setFeries,feriesLd]=useSt('feries7',[]);
  const[settings,setSettings,settingsLd]=useSt('settings7',{regime:'alsace-moselle'});
  const[cpSoldes,setCpSoldes,cpSoldesLd]=useSt('cpSoldes7',[]);
  const[arretsMaladie,setArretsMaladie,arretsMaladieLd]=useSt('arretsMaladie7',[]);
  const[factures,setFactures,facturesLd]=useSt('factures7',[],{defer:true});
  const[compteursFacture,setCompteursFacture,compteursFactureLd]=useSt('compteursFacture7',{});
  const[alertesSante,setAlertesSante,alertesSanteLd]=useSt('alertesSante7',[]);
  const[statutAppts,setStatutAppts,statutApptslLd]=useSt('statutAppts7',{});
  const[filPartage,setFilPartage,filPartageLd]=useSt('filPartage7',[],{defer:true});
  const[filProprio,setFilProprio,filProprioLd]=useSt('filProprio7',[],{defer:true});
  const[assist,setAssist]=useSt('assist7',[]);  // Demandes d'aide terrain (encadrement renforcé) — trace persistante + bandeau (non gaté au chargement : défaut [])
  const[dureeAnom,setDureeAnom]=useSt('dureeAnom7',[]);  // Seuils qualité (v154) : signalements d'empêchement à clôturer un ménage hors durée min/max — non gaté, même logique qu'assist7
  const[syncano,setSyncano]=useSt('syncano7',[]);  // Journal des anomalies de sync non résolues (SyncErrorBanner) — non gaté, même logique qu'assist7
  const[devis,setDevis,devisLd]=useSt('devis7',[],{defer:true});                      // Propositions tarifaires (append-only, archivage jamais suppression)
  const[devisConfig,setDevisConfig,devisConfigLd,setDevisConfigSynced]=useSt('devisConfig7',null);  // Catalogue de prestations + compteur DEV-{année}-{NNN}
  const[pauseQR,setPauseQR]=useSt('pauseQR7',[]);  // Journal Q/R modale « pause oubliée » Lot D (Oui ET Non) — indépendant de pauseAnom (qui reste le workflow de vérif des Non sur als7)
  // ═══ Consentement RGPD salariés (SPEC_rgpd_consentement_v1.md, 17/08/2026) ═══
  // rgpdDoc7 : document versionné (texte figé, jamais réédité en place — une évolution
  // du texte crée une nouvelle version). rgpdAck7 : accusés append-only, AUCUNE fonction
  // d'édition/suppression nulle part dans le dépôt (c'est ça qui garantit
  // l'irréversibilité, pas un verrou technique — voir RgpdInfo.jsx). rgpdPending7 :
  // premier vu par salarié/version, sert au calcul du délai de 24h. Les trois, non
  // gatées au chargement, même logique qu'assist7/dureeAnom7 ci-dessus.
  const[rgpdDoc,setRgpdDoc]=useSt('rgpdDoc7',RGPD_DOC_V1);
  const[rgpdAck,setRgpdAck]=useSt('rgpdAck7',[]);
  const[rgpdPending,setRgpdPending]=useSt('rgpdPending7',[]);
  // ═══ Module Consigne (SPEC_consigne_v2 / CONCEPTION_consigne_ecrans_v1) ═══
  // Data-layer, option A : 4 clés fines préfixées « consigne ». Réglages/catalogue
  // = config (petit, chargement eager). Dépôts/locations = flux (chaud → defer,
  // comme sejours/controles/factures). AUCUNE écriture au chargement : useSt garde
  // le défaut EN MÉMOIRE tant qu'aucun setSt ne survient (branche « aucune ligne en
  // base » de tryLoad : lastSync = défaut → l'effet d'écriture skippe). Les lignes ne
  // sont créées en base qu'à la 1re vraie configuration en Réglages. Le CA
  // (consigneCaLignes7_AAAA, partitionné année) est accédé à part, pas ici.
  const[consigneSettings,setConsigneSettings]=useSt('consigneSettings7',CONSIGNE_SETTINGS_DEFAUT);
  const[consigneCatalogue,setConsigneCatalogue]=useSt('consigneCatalogue7',CONSIGNE_CATALOGUE_DEFAUT);
  const[consigneDepots,setConsigneDepots]=useSt('consigneDepots7',[],{defer:true});
  const[consigneLocations,setConsigneLocations]=useSt('consigneLocations7',[],{defer:true});
  // Facturation mensuelle Consigne (v201) — append-only, meme patron que `factures7`
  // (proprietaires) mais separe : serie FC-CO, jamais melange avec la serie FC.
  const[consigneFactures,setConsigneFactures]=useSt('consigneFactures7',[],{defer:true});
  const[sec,setSec]=useState('gen');

  // ⚠️ RÈGLE DES HOOKS — ces deux effets DOIVENT rester au-dessus de tout `return`
  // anticipé (page publique /exp, RecoveryScreen, LoginScreen). Ils étaient placés
  // en dessous jusqu'au 28/07/2026 : dès que `sess` passait de non-null à null (ou
  // l'inverse), React voyait le nombre de hooks changer d'un rendu à l'autre et
  // plantait en page blanche (`TypeError` illisible dans react-vendor minifié).
  // Ne jamais redescendre ces hooks sous un return conditionnel.

  // ═══ Bus de notifications : sync du contexte ═══
  // Le bus est en dehors de React, on lui pousse les listes à jour à chaque changement.
  React.useEffect(()=>{
    if(!sess)return;
    setNotifContext({notifConfig,staff,gerants,prov,clients,sess});
  },[notifConfig,staff,gerants,prov,clients,sess]);

  // File d'attente locale (SPEC_file_locale_v1 §5.4) : purge des entrées confirmées
  // de plus de 30 j au démarrage, avant tout affichage de l'écran de reprise. Non bloquant.
  useEffect(()=>{ purgeExpired(); },[]);

  // Chantier B (01/08/2026) : rejeu silencieux als7/controles7 dès que ces deux clés
  // sont chargées (pas avant : tryLoad doit avoir fini pour que supaGet/setControles
  // partent d'un état serveur à jour, sinon risque de merge sur une base incomplète).
  // Garde par ref : ne doit jamais repartir à chaque re-render, une seule fois par
  // session, quand alsLd/controlesLd passent à true.
  const autoReplayDone=useRef(false);
  useEffect(()=>{
    if(autoReplayDone.current)return;
    if(!(alsLd&&controlesLd))return;
    autoReplayDone.current=true;
    autoReplayAls7({setAlsSynced});
    autoReplayControles7({setControles,setRep});
  },[alsLd,controlesLd]);

  // ═══ Migration contrats[] (SPEC_personnel_contrats_v1 §3 — Étape 1A, 02/08/2026) ═══
  // Une seule fois par session, dès que staff7 est chargé. Construit contrats[0]
  // à partir des anciens champs plats pour toute fiche qui n'en a pas encore.
  //
  // ⚠️ Étape 1A : on AJOUTE `contrats[]` sans toucher aux champs plats. Leur
  // suppression est l'Étape 1C, une fois TOUS les lecteurs repointés (Étape 1B) —
  // supprimer avant reviendrait à mettre la paie à zéro entre deux commits.
  //
  // Read-before-write : le supaGet juste avant l'écriture évite d'écraser
  // l'édition concurrente d'un autre admin (saveS, lui, n'a pas cette garde —
  // dette préexistante, hors mandat, signalée à Claire).
  // On passe ensuite par setStaff() plutôt qu'un supaSet direct : l'écriture de
  // useSt bénéficie du retry backoff et du SyncErrorBanner, et c'est une seule
  // écriture au lieu des deux du pattern delS().
  //
  // No-op sur : fiche déjà migrée, fiche sans `hire` exploitable, gérants (autre
  // clé, jamais concernés). Les salariés ARCHIVÉS sont migrés comme les autres —
  // leur historique doit rester lisible dans le futur registre du personnel.
  // Échec réseau : pas de nouvelle tentative dans la session (contratsOf() couvre
  // le cas en lecture), la migration repartira au prochain chargement.
  const migContratsDone=useRef(false);
  useEffect(()=>{
    if(migContratsDone.current)return;
    if(!staffLd)return;
    migContratsDone.current=true;
    (async()=>{
      try{
        const current=await supaGet('staff7');
        if(!Array.isArray(current)||!current.length)return;
        const{updated,changed,count}=migrerContrats(current);
        if(!changed)return;
        console.log('[migration contrats] contrats[] créé pour',count,'salarié(s)');
        setStaff(updated);
      }catch(e){
        console.warn('[migration contrats] échec, retentée au prochain chargement :',e?.message||e);
      }
    })();
  },[staffLd]);

  // Migration idempotente du compteur de factures (R2) : ancienne forme plate
  // { annee: n } -> forme imbriquee par serie { FC: { annee: n } }, pour laisser
  // le module Consigne ajouter sa propre serie sans toucher la sequence legale FC.
  // Meme patron que la migration contrats ci-dessus : garde useRef, gate sur le
  // chargement, et READ-BEFORE-WRITE via supaGet (le compteur legal est une clef
  // sensible, ecrite aussi par Franck en admin simultane — on migre la valeur la
  // plus fraiche, jamais une valeur perimee du state). migrerCompteurs est PURE et
  // idempotente : 17 reste 17 sous FC, aucun saut ni regression de numerotation.
  // Echec reseau : pas de nouvelle tentative dans la session (les helpers tolerent
  // la forme plate en lecture), la migration repartira au prochain chargement.
  const migCompteursDone=useRef(false);
  useEffect(()=>{
    if(migCompteursDone.current)return;
    if(!compteursFactureLd)return;
    migCompteursDone.current=true;
    (async()=>{
      try{
        const current=await supaGet('compteursFacture7');
        const{updated,changed}=migrerCompteurs(current);
        if(!changed)return;
        console.log('[migration compteurs] compteursFacture7 : forme plate -> imbriquee par serie (FC)');
        setCompteursFacture(updated);
      }catch(e){
        console.warn('[migration compteurs] echec, retentee au prochain chargement :',e?.message||e);
      }
    })();
  },[compteursFactureLd]);

  // Retour d'email « Définir mon mot de passe » : priorité sur tout le reste.
  // Page publique simulateur expédition
  // Page publique simulateur expédition objets trouvés
  const expMatch = window.location.pathname.match(/^\/exp\/([a-z0-9]+)$/i);
  if(expMatch) return <SimulateurExpPublic token={expMatch[1]} />;

  // Page publique d'acceptation d'une proposition tarifaire (module devis, Lot 2)
  const devisMatch = window.location.pathname.match(/^\/devis\/([a-z0-9]+)$/i);
  if(devisMatch) return (
    <React.Suspense fallback={<div style={{
      minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',
      fontFamily:'system-ui,sans-serif',color:'#2a2520'
    }}>Chargement…</div>}>
      <DevisPublic token={devisMatch[1]} />
    </React.Suspense>
  );

  // Page publique de PAIEMENT d'une reservation prise par telephone : /consigne?payer=CODE (lot T3). AVANT la reservation en ligne,
  // qui partage le meme chemin /consigne. Sans parametre payer, c'est la reservation en ligne habituelle (inchange).
  if(/^\/consigne\/?$/i.test(window.location.pathname) && new URLSearchParams(window.location.search).has('payer')) return (
    <React.Suspense fallback={<div style={{
      minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',
      fontFamily:'system-ui,sans-serif',color:'#2a2520'
    }}>Chargement…</div>}>
      <PagePaiementResa />
    </React.Suspense>
  );

  // Page publique de reservation en ligne de la Consigne (module Consigne, ecran ⑥)
  if(/^\/consigne\/?$/i.test(window.location.pathname)) return (
    <React.Suspense fallback={<div style={{
      minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',
      fontFamily:'system-ui,sans-serif',color:'#2a2520'
    }}>Chargement…</div>}>
      <ConsigneClientUI />
    </React.Suspense>
  );

  // Écran comptoir « Ma Consigne » (Dépôt/Retrait/Retour uniquement, SANS
  // connexion — décision Claire, 14/09/2026, voir ConsigneComptoir.jsx).
  // AVANT le login : sinon quelqu'un déjà connecté sur ce même navigateur
  // (admin, gérant…) verrait le comptoir se substituer au menu attendu.
  if(/^\/consigne-comptoir\/?$/i.test(window.location.pathname)) return (
    <React.Suspense fallback={<div style={{
      minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',
      fontFamily:'system-ui,sans-serif',color:'#2a2520'
    }}>Chargement…</div>}>
      <ConsigneComptoir />
    </React.Suspense>
  );

  if(RECOVERY_MODE) return <RecoveryScreen/>;

  if(!sess) return <LoginScreen onSel={setSess}/>;

  // ═══ ÉCRAN DE CHARGEMENT — bloque toute interaction tant que les tables critiques
  // ne sont pas chargées depuis Supabase. Empêche les actions utilisateurs trop tôt
  // (qui pourraient être écrasées par la lecture initiale arrivant en différé).
  const allLoaded=staffLd&&gerantsLd&&provLd&&clientsLd&&biensLoaded&&stLd&&pjLd
    &&leaveLd&&unavailLd&&reqLd&&msgsLd&&msgsLuLd&&journalLd&&tcLd&&alsLd&&repLd&&sejoursLd&&objetsLd
    &&controlesLd&&commentairesLd&&notifConfigLd&&feriesLd&&facturesLd&&statutApptslLd&&otBacsLd&&tarifsExpLd&&settingsLd&&cpSoldesLd&&arretsMaladieLd&&maintenanceLd&&devisLd&&devisConfigLd;
  if(!allLoaded) return <div style={{
    position:'fixed',top:0,left:0,right:0,bottom:0,background:'var(--bg)',
    display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',
    gap:18,zIndex:9999,padding:24
  }}>
    <div style={{fontSize:48}}>⏳</div>
    <div className="D" style={{fontSize:20,color:'var(--or)',textAlign:'center'}}>Chargement des données…</div>
    <div style={{fontSize:13,color:'var(--si)',textAlign:'center',maxWidth:340}}>
      Synchronisation avec le serveur. Quelques secondes, et ça démarre !
    </div>
  </div>;

  // Mode maintenance : bloque l'accès pour les profils ciblés (avec exceptions par compte).
  // L'admin n'est jamais bloqué (garde-fou volontaire : c'est le seul moyen de désactiver
  // le mode depuis l'appli).
  if(sess.type!=='admin' && isBlocked(maintenance,sess.type,sess.id)){
    return <MaintenanceScreen data={maintenance}/>;
  }

  const aMap={admin:'var(--ye)',staff:'var(--mn)',provider:'var(--or)',client:'var(--pk)',gerant:'var(--bl)'};
  const lMap={admin:'Admin',staff:'Salarié',provider:'Prestataire',client:'Propriétaire',gerant:'Direction'};
  let name='Admin';
  if(sess.type==='staff'){const s=staff.find(s=>s.id===sess.id);name=s?fname(s):'?';}
  else if(sess.type==='gerant') name=gerants.find(g=>g.id===sess.id)?.name||'?';
  else if(sess.type==='provider') name=prov.find(p=>p.id===sess.id)?.name||'?';
  else if(sess.type==='client') name=clients.find(c=>c.id===sess.id)?.name||'?';

  const ur=sess.type==='admin'?msgs.filter(m=>!m.ra&&m.ft!=='bot'&&m.ft!=='admin').length:msgs.filter(m=>m.tid===`${sess.type}:${sess.id}`&&!m.ru).length;
  const P={staff,setStaff,gerants,setGerants,prov,setProv,clients,setClients,biens,setBiens,biensLoaded,st,setSt,pj,setPj,leave,setLeave,unavail,setUnavail,req,setReq,msgs,setMsgs,msgsLu,setMsgsLu,journal,setJournal,logAction:(e)=>logAction(setJournal,e),tc,setTc,setTcSynced,als,setAls,setAlsSynced,rep,setRep,sejours,setSejours,objets,setObjets,otBacs,setOtBacs,tarifsExp,setTarifsExp,controles,setControles,commentaires,setCommentaires,viewAs,sess,userName:name,feries,setFeries,settings,setSettings,cpSoldes,setCpSoldes,arretsMaladie,setArretsMaladie,factures,setFactures,compteursFacture,setCompteursFacture,alertesSante,setAlertesSante,statutAppts,setStatutAppts,filPartage,setFilPartage,filProprio,setFilProprio,assist,setAssist,dureeAnom,setDureeAnom,syncano,setSyncano,pauseQR,setPauseQR,devis,setDevis,devisConfig,setDevisConfig,setDevisConfigSynced,rgpdDoc,setRgpdDoc,rgpdAck,setRgpdAck,rgpdPending,setRgpdPending,consigneSettings,setConsigneSettings,consigneCatalogue,setConsigneCatalogue,consigneDepots,setConsigneDepots,consigneLocations,setConsigneLocations,consigneFactures,setConsigneFactures};
  const unreadMsgs=msgs.filter(m=>!m.ra&&m.ft!=='bot'&&m.ft!=='admin').length;
  const nonLusProprioStatut=(filProprio||[]).filter(m=>!m.lu_admin).length;
  const pendLeave=leave.filter(l=>l.st==='pending').length;
  const geoAnoms=als.filter(l=>l.ok===false&&!l.archived).length;

  return(<div style={{maxWidth:1460,margin:'0 auto',padding:'18px 18px'}}>
    <SyncErrorBanner sess={sess} name={name}/>
    <QueueRecovery rep={rep} setRep={setRep} biens={biens} staff={staff} sess={sess}/>
    {impersonating&&<div style={{background:'#fff4d6',border:'2px solid #e5c977',borderRadius:10,padding:'10px 14px',marginBottom:12,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
      <div style={{fontSize:13}}>
        👁️ <strong>Mode "Voir comme"</strong> — Vous consultez l'interface de <strong>{name}</strong> ({lMap[sess.type]}). Aucune action n'est enregistrée comme étant la sienne, c'est juste un aperçu.
      </div>
      <button onClick={stopViewAs} className="btn bor sm">← Retour à mon compte admin</button>
    </div>}
    <div className="topbar">
      <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
        <div className="D" style={{fontSize:19}}>La Conciergerie</div>
        <span className="pill" style={{background:aMap[sess.type],color:sess.type==='admin'?'var(--ink)':'white',textTransform:'uppercase',letterSpacing:'.08em',fontSize:10}}>{lMap[sess.type]}</span>
        <span style={{fontSize:12,color:'var(--si)'}}>· {name}</span>
        {ur>0&&<span className="bc">{ur}</span>}
      </div>
      <div style={{display:'flex',alignItems:'center',gap:6}}>
        {/* Toggle lecture vocale — visible pour staff et direction (les salariés sont la cible). 
            Aussi visible pour admin (pratique pour tester). Caché pour propriétaires/prestataires. */}
        {(sess.type==='staff'||sess.type==='gerant'||sess.type==='admin')&&<VoiceToggleButton voice={voiceMode} setVoice={setVoiceMode}/>}
        {sess.type!=='client'&&<NotificationButton sess={sess}/>}
        <button onClick={()=>setShowChangePwd(true)} className="btn bgh sm" title="Changer mon mot de passe" style={{fontSize:13}}>🔑</button>
        <button onClick={()=>{try{localStorage.removeItem('supa_session');}catch{}window.location.href=window.location.pathname;}} className="btn bgh sm"><Ic n="out" c="w-3.5 h-3.5"/>Se déconnecter</button>
      </div>
    </div>
    {/* Listener global d'appui long (uniquement actif si voiceMode=true) */}
    <VoiceListener active={voiceMode}/>

    {/* Toasts in-app : rendus pour TOUS les rôles. Le store ne contient, pour un
        non-admin, que les notifs dont il est destinataire (canal bandeau_inapp).
        Pour l'admin, ce sont les toasts de debug. Le composant gère l'affichage. */}
    <NotifToastStack/>
    {showChangePwd&&<ChangePasswordModal onClose={()=>setShowChangePwd(false)}/>}

    {sess.type==='admin'&&(<div>
      <PendingReportBanner als={als} rep={rep} st={st} staff={staff} gerants={gerants} biens={biens} onGo={setSec}/>
      <AssistanceBanner assist={assist} setAssist={setAssist} staff={staff} gerants={gerants} sess={sess} onGo={setSec}/>
      <DureeAnomBanner dureeAnom={dureeAnom} setDureeAnom={setDureeAnom} staff={staff} gerants={gerants} sess={sess} onGo={setSec} als={als} rep={rep} controles={P.controles}/>
      <AdminNav sec={sec} setSec={setSec}
        ctGen={(()=>{const luSet=new Set(msgsLu||[]);return msgs.filter(m=>!luSet.has(m.id)&&m.ft!=='bot'&&m.ft!=='admin').length+req.filter(r=>r.st==='pending').length+(commentaires||[]).filter(c=>!c.lu&&c.texte).length;})()}
        ctSta={nonLusProprioStatut}
        ctRh={pendLeave+geoAnoms}
        ctObj={(()=>{const now3m=new Date();now3m.setMonth(now3m.getMonth()-3);return(P.objets||[]).filter(o=>o.statut==='stored'&&new Date(o.date)>=now3m).length;})()}
        ctRap={0}
        ctCtrl={(()=>{const todayS=(new Date()).toISOString().slice(0,10);return(P.rep||[]).filter(r=>{if(!r.sub||r.hidAdmin)return false;const ctrl=(P.controles||[]).find(c=>c.repId===r.id);if(ctrl?.verdict==='pret'||ctrl?.verdict==='nonpret'||ctrl?.verdict==='abandonne')return false;const soumisAujourdhui=r.sub?.startsWith(todayS)||r.date===todayS;const estReporte=ctrl?.verdict==='reporter';return soumisAujourdhui||estReporte;}).length;})()}
        ctAno={(()=>{const since=Date.now()-24*60*60*1000;return(syncano||[]).filter(a=>(a.lastTs||0)>=since).length;})()}
        ctFinJ={(journal||[]).filter(e=>e.action==='fin_journee_anomalie'&&!e.traite).length}
        ctFac={nbFacturesEnRetard(factures)}
        ctMaint={maintenance?.on?1:0}
      />
      {(sec==='gen'||sec==='com'||sec==='msg')&&<VueGen {...P} onNav={setSec} initialSub={sec==='com'?'com':sec==='msg'?'msg':'db'}/>}
      {sec==='rh'&&<React.Suspense fallback={<div style={{padding:24,textAlign:'center',color:'#888'}}>Chargement…</div>}><SecRH {...P}/></React.Suspense>}
      {sec==='pre'&&<React.Suspense fallback={<div style={{padding:24,textAlign:'center',color:'#888'}}>Chargement…</div>}><SecPrest {...P}/></React.Suspense>}
      {sec==='pro'&&<React.Suspense fallback={<div style={{padding:24,textAlign:'center',color:'#888'}}>Chargement…</div>}><SecProp {...P}/></React.Suspense>}
      {sec==='fac'&&<SecFacturation {...P}/>}
      {sec==='dev'&&<React.Suspense fallback={<div style={{padding:24,textAlign:'center',color:'#888'}}>Chargement…</div>}><SecDevis devis={P.devis||[]} setDevis={P.setDevis} devisConfig={P.devisConfig} setDevisConfig={P.setDevisConfig} setDevisConfigSynced={P.setDevisConfigSynced} clients={clients} setClients={setClients} biens={biens} setBiens={setBiens} logAction={P.logAction}/></React.Suspense>}
      {sec==='devc'&&<React.Suspense fallback={<div style={{padding:24,textAlign:'center',color:'#888'}}>Chargement…</div>}><SecDevisConfig devisConfig={P.devisConfig} setDevisConfig={P.setDevisConfig}/></React.Suspense>}
      {sec==='ts'&&<TaxeSejourView mode="admin" sess={{type:'admin'}} sejours={P.sejours||[]} biens={biens} clients={clients}/>}
      {sec==='sta'&&<React.Suspense fallback={<div style={{padding:24,textAlign:'center',color:'#888'}}>Chargement…</div>}><SecStatutAppts biens={biens} clients={clients} sejours={P.sejours||[]} rep={rep} controles={P.controles||[]} statutAppts={statutAppts} setStatutAppts={setStatutAppts} sess={sess} st={st} als={als} filPartage={filPartage} setFilPartage={setFilPartage} filProprio={filProprio} setFilProprio={setFilProprio} commentaires={commentaires} onNav={setSec} logAction={P.logAction}/></React.Suspense>}
      {/* Calendrier des SÉJOURS (occupation mensuelle). À ne pas confondre avec sec==='cal',
          qui est SecCalendrier = gestion des jours fériés. Même composant que l'onglet
          📆 Séjours de l'interface Direction : une seule source, deux points d'entrée. */}
      {sec==='sej'&&<div><div className="D" style={{fontSize:18,marginBottom:12}}>Calendrier des séjours</div><CalendrierSejours biens={biens} sejours={P.sejours||[]} clients={clients}/></div>}
      {sec==='obj'&&<SecObjets objets={P.objets||[]} setObjets={P.setObjets} biens={biens} clients={clients} sejours={P.sejours||[]} otBacs={P.otBacs||[]} setOtBacs={P.setOtBacs} tarifsExp={P.tarifsExp}/>}
      {sec==='exp'&&<div style={{padding:'16px'}}><div style={{fontWeight:700,fontSize:16,marginBottom:16}}>⚙️ Tarifs expédition objets trouvés</div><AdminGrilleTarifs tarifsExp={P.tarifsExp} setTarifsExp={P.setTarifsExp}/></div>}
      {sec==='rap'&&<SecRapports rep={rep} setRep={setRep} biens={biens} staff={staff} gerants={P.gerants||[]} controles={P.controles||[]} als={als} setControles={P.setControles} sess={{id:'admin',name:sess?.email||'Admin',type:'admin'}} dureeAnom={P.dureeAnom}/>}
      {sec==='ctrl'&&<SecControles controles={P.controles||[]} setControles={P.setControles} rep={rep} setRep={setRep} biens={biens} staff={staff} gerants={P.gerants||[]} sess={{id:'admin',name:sess?.email||'Admin',type:'admin'}} logAction={P.logAction} als={als} dureeAnom={P.dureeAnom}/>}
      {sec==='anos'&&<SecAnomaliesSync syncano={P.syncano||[]} staff={staff} gerants={gerants}/>}
      {sec==='finj'&&<SecFinJournee journal={journal} setJournal={setJournal} sess={sess}/>}
      {sec==='notif'&&<SecNotifs notifConfig={notifConfig} setNotifConfig={setNotifConfig}/>}
      {sec==='cal'&&<SecCalendrier feries={feries} setFeries={setFeries}/>}
      {sec==='ana'&&<React.Suspense fallback={<div style={{padding:24,textAlign:'center',color:'#888'}}>Chargement…</div>}><SecAnalyses {...P}/></React.Suspense>}
      {sec==='log'&&<React.Suspense fallback={<div style={{padding:24,textAlign:'center',color:'#888'}}>Chargement…</div>}><SecJournal journal={journal} biens={biens} clients={clients}/></React.Suspense>}
      {sec==='maint'&&<SecMaintenance maintenance={maintenance} setMaintenance={setMaintenance} logAction={P.logAction} sess={sess} staff={staff} gerants={gerants} prov={prov} clients={clients}/>}
      {sec==='consigne'&&<React.Suspense fallback={<div style={{padding:24,textAlign:'center',color:'#888'}}>Chargement…</div>}><SecConsigne {...P}/></React.Suspense>}
    </div>)}
    {sess.type==='staff'    &&<StaffUI  sess={sess} {...P}/>}
    {sess.type==='gerant'   &&<GerantUI sess={sess} {...P}/>}
    {sess.type==='provider' &&<ProvUI   sess={sess} {...P}/>}
    {sess.type==='client'   &&<ClientUI sess={sess} {...P}/>}

    <footer style={{marginTop:50,paddingTop:16,borderTop:'1px solid var(--li)',display:'flex',justifyContent:'space-between',fontSize:10,color:'var(--si)'}}>
      <span className="D" style={{fontSize:13}}>La Conciergerie · Colmar</span>
      <span>v9 · Supabase Auth + Data</span>
    </footer>
  </div>);
}

export { MemoryGauge, ErrorBlackbox, App };

